/* ==========================================================================
   Paola & Joy — Register your interest → Klaviyo
   --------------------------------------------------------------------------
   Subscribes the visitor to a Klaviyo list and stamps the sign-up with where
   it came from, using the two public client endpoints:

     POST /client/subscriptions  → list membership + email marketing consent
     POST /client/events         → a custom metric a flow can be triggered from

   Both take the public API key only (the same one Klaviyo's own onsite script
   puts in the page), never a private key, and both answer 202 on success.

   The subscribe is what must succeed. The event is extra: it carries the
   history that profile properties overwrite, so a failure there is swallowed
   rather than shown to the customer as a failed sign-up.
   ========================================================================== */

(function () {
  'use strict';

  var REVISION = '2026-07-15';
  var BASE = 'https://a.klaviyo.com/client/';

  /* Deliberately loose: the only thing worth catching here is a typo so
     obvious it cannot be an address. Klaviyo does the real validation. */
  function looksLikeEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  function post(path, companyId, body) {
    return fetch(BASE + path + '/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: REVISION
      },
      body: JSON.stringify(body)
    });
  }

  function memoryKey(el) {
    return 'pj:interest:' + el.dataset.list + ':' + (el.dataset.product || el.dataset.source);
  }

  function remembered(el) {
    try { return window.localStorage.getItem(memoryKey(el)) === '1'; }
    catch (e) { return false; }
  }

  function remember(el) {
    try { window.localStorage.setItem(memoryKey(el), '1'); }
    catch (e) { /* private mode, nothing to do */ }
  }

  function init(el) {
    var form = el.querySelector('[data-pj-interest-form]');
    var input = el.querySelector('[data-pj-interest-email]');
    var button = el.querySelector('[data-pj-interest-submit]');
    var msg = el.querySelector('[data-pj-interest-msg]');
    if (!form || !input || !msg) return;

    function say(text, state) {
      msg.textContent = text;
      if (state) { el.dataset.state = state; } else { delete el.dataset.state; }
    }

    if (remembered(el)) say(el.dataset.success, 'done');

    /* Properties are assembled at submit time, not at load: on the Robyn page
       the section script keeps data-extra in step with the chosen colourway,
       so we want whatever is current when they actually sign up. */
    function properties() {
      var props = {
        'Interest Source': el.dataset.source,
        'Interest Registered': true
      };
      if (el.dataset.product) props['Interest Product'] = el.dataset.product;

      try {
        var extra = JSON.parse(el.dataset.extra || '{}');
        Object.keys(extra).forEach(function (k) { props[k] = extra[k]; });
      } catch (e) { /* malformed extras should not cost us the sign-up */ }

      return props;
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (el.dataset.busy === 'true' || el.dataset.state === 'done') return;

      var email = (input.value || '').trim();
      if (!looksLikeEmail(email)) {
        say('That email does not look right — mind checking it?', 'error');
        input.focus();
        return;
      }

      var company = el.dataset.company;
      var props = properties();
      var label = button ? button.textContent : '';

      el.dataset.busy = 'true';
      if (button) { button.disabled = true; button.textContent = 'Sending…'; }
      say('', null);

      post('subscriptions', company, {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: el.dataset.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: props,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: el.dataset.list } }
          }
        }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Klaviyo returned ' + res.status);

          remember(el);
          say(el.dataset.success, 'done');
          input.value = '';

          if (el.dataset.metric) {
            post('events', company, {
              data: {
                type: 'event',
                attributes: {
                  properties: props,
                  metric: { data: { type: 'metric', attributes: { name: el.dataset.metric } } },
                  profile: { data: { type: 'profile', attributes: { email: email } } }
                }
              }
            }).catch(function () { /* the sign-up already landed */ });
          }
        })
        .catch(function () {
          say('That did not go through. Try again in a moment, or email us and we will add you.', 'error');
          if (button) { button.disabled = false; button.textContent = label; }
        })
        .then(function () {
          el.dataset.busy = 'false';
          if (el.dataset.state === 'done' && button) button.textContent = label;
        });
    });
  }

  function boot() {
    document.querySelectorAll('[data-pj-interest]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-pj-interest]').forEach(init);
  });
})();
